// AES-GCM encryption for localStorage note drafts, using a per-employment key delivered from
// the server on the employment record (employment.draftEncryptionKey, base64 of 32 random
// bytes). The key lives only in memory (never persisted client-side), so a localStorage copy
// separated from a live session can't be decrypted. It is NOT protection against an attacker
// with an active authenticated session — they can read the key from the store / API.
//
// Payload format is `"<iv-base64>.<ciphertext-base64>"`. GCM also authenticates (tamper-evident).

const keyCache = {} // rawBase64 -> Promise<CryptoKey>

const toB64 = (bytes) => btoa(String.fromCharCode(...bytes))
const fromB64 = (s) => Uint8Array.from(atob(s), (c) => c.charCodeAt(0))

function importKey(rawBase64) {
  if (!keyCache[rawBase64]) {
    keyCache[rawBase64] = crypto.subtle.importKey(
      'raw', fromB64(rawBase64), 'AES-GCM', false, ['encrypt', 'decrypt']
    )
  }
  return keyCache[rawBase64]
}

export async function encrypt(plain, rawBase64) {
  const key = await importKey(rawBase64)
  const iv = crypto.getRandomValues(new Uint8Array(12))
  const encryptedData = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(plain))
  return `${toB64(iv)}.${toB64(new Uint8Array(encryptedData))}`
}

export async function decrypt(payload, rawBase64) {
  const key = await importKey(rawBase64)
  const [iv, encryptedData] = String(payload).split('.')
  const data = await crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromB64(iv) }, key, fromB64(encryptedData))
  return new TextDecoder().decode(data)
}
